import React, { useState } from "react";
import { Modal, Form, Button, Alert, ProgressBar } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import { uploadToR2 } from '../utils/r2Upload';

function inputValue(e) {
  const el = e && (e.target || e.currentTarget);
  return el && el.value != null ? el.value : '';
}

function firstFile(e) {
  const el = e && (e.target || e.currentTarget);
  const files = el && el.files;
  return files && files[0] ? files[0] : null;
}

/**
 * Tek bildirim penceresi: metin veya isteğe bağlı fotoğraflı bildirim.
 * Anasayfa ve hızlı cevapsız profil kartı aynı bileşeni kullanır.
 */
export default function SendUserNotificationModal({
  show,
  onHide,
  targetUserId,
  targetUserName
}) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const reset = () => {
    setTitle('');
    setMessage('');
    setImageFile(null);
    setUploadProgress(0);
    setError(null);
    setSending(false);
  };

  const handleClose = () => {
    if (sending) return;
    reset();
    if (onHide) onHide();
  };

  const handleSend = async () => {
    if (!targetUserId) {
      setError('Kullanıcı seçilemedi.');
      return;
    }
    if (!title.trim() || !message.trim()) {
      setError('Başlık ve mesaj zorunludur.');
      return;
    }

    setSending(true);
    setError(null);
    try {
      let imageUrl = null;
      if (imageFile) {
        const ext = (imageFile.name.split('.').pop() || 'jpg').toLowerCase();
        const fileName = `notifications/user_${targetUserId}_${Date.now()}.${ext}`;
        imageUrl = await uploadToR2(imageFile, fileName, (progress) => {
          setUploadProgress(progress);
        });
      }

      const res = await api.post('/notifications/send', {
        title: title.trim(),
        message: message.trim(),
        userIds: [targetUserId],
        imageUrl: imageUrl || null
      });

      if (res.data?.success) {
        alert(
          imageUrl
            ? `Fotoğraflı bildirim gönderildi.\nBaşarılı: ${res.data.sentCount || 0}`
            : `Bildirim gönderildi.\nBaşarılı: ${res.data.sentCount || 0}`
        );
        reset();
        if (onHide) onHide();
      } else {
        setError(res.data?.error || 'Bildirim gönderilemedi.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Bildirim gönderilemedi.');
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          Bildirim Gönder{targetUserName ? ` - ${targetUserName}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="text-muted small mb-3">
          Fotoğraf eklerseniz fotoğraflı bildirim, eklemezseniz normal bildirim gider.
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Başlık</Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => { const val = inputValue(e); setTitle(val); }}
            placeholder="Bildirim başlığı"
            disabled={sending}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Mesaj</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={message}
            onChange={(e) => { const val = inputValue(e); setMessage(val); }}
            placeholder="Bildirim mesajı..."
            disabled={sending}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Fotoğraf (opsiyonel)</Form.Label>
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e) => { const file = firstFile(e); setImageFile(file); }}
            disabled={sending}
          />
          {imageFile && (
            <small className="text-info d-block mt-1">Seçilen: {imageFile.name}</small>
          )}
        </Form.Group>
        {uploadProgress > 0 && uploadProgress < 100 && (
          <ProgressBar now={uploadProgress} label={`${Math.round(uploadProgress)}%`} className="mt-2" />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={sending}>
          İptal
        </Button>
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={sending || !title.trim() || !message.trim()}
        >
          {sending ? <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> : null}
          {imageFile ? 'Fotoğraflı Gönder' : 'Gönder'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
